import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { AlertTriangle, Check, Info, Loader2, Minimize2, RotateCcw, X } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import mediaService from '@services/mediaService';
import type {
  MediaAsset,
  MediaCompression,
  MediaCompressionAcceptResult,
  MediaCompressionMeta,
  MediaCompressionPresetKey,
} from '@/types/media';
import { formatBytes } from '@/utils/formatters';
import {
  buildCompressionRequest,
  compareWidth,
  compressionSummary,
  formatGain,
  imageSpec,
  isCompressionRunning,
  LARGE_IMAGE_BYTES,
  syncedScroll,
  type CompareZoom,
} from '@/utils/mediaCompression';

interface Props {
  media: MediaAsset;
  isOpen: boolean;
  onClose: () => void;
  /** "Usar a nova" concluído: a mídia já tem o arquivo compactado. */
  onAccepted: (result: MediaCompressionAcceptResult) => void;
}

/** Fundo xadrez: mostra a transparência dos PNG. */
const checker: React.CSSProperties = {
  backgroundColor: '#f8fafc',
  backgroundImage:
    'linear-gradient(45deg,#e2e8f0 25%,transparent 25%),linear-gradient(-45deg,#e2e8f0 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e2e8f0 75%),linear-gradient(-45deg,transparent 75%,#e2e8f0 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
};

const ZOOMS: { key: CompareZoom; label: string }[] = [
  { key: 'fit', label: 'Ajustar' },
  { key: '100', label: '100%' },
  { key: '200', label: '200%' },
];

interface PaneProps {
  label: string;
  badge?: React.ReactNode;
  spec: string;
  url?: string;
  zoom: CompareZoom;
  width?: number;
  boxRef: React.RefObject<HTMLDivElement>;
  onScroll: () => void;
}

/** Um lado da comparação: cabeçalho com tamanho/dimensões e a imagem (ajustada ou ampliada). */
const Pane: React.FC<PaneProps> = ({ label, badge, spec, url, zoom, width, boxRef, onScroll }) => (
  <div className="flex-1 min-w-0 min-h-0 flex flex-col rounded-lg border border-slate-200 overflow-hidden">
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-white">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      <span className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        {spec}
        {badge}
      </span>
    </div>
    <div
      ref={boxRef}
      onScroll={onScroll}
      style={checker}
      className={clsx('h-[42vh] lg:h-auto lg:flex-1 min-h-0', zoom === 'fit' ? 'overflow-hidden flex items-center justify-center' : 'overflow-auto')}
    >
      {url ? (
        <img
          src={url}
          alt={label}
          draggable={false}
          className={zoom === 'fit' ? 'max-w-full max-h-full object-contain' : 'block max-w-none'}
          style={zoom === 'fit' ? undefined : { width }}
        />
      ) : (
        <p className="p-6 text-sm text-slate-400">Imagem indisponível.</p>
      )}
    </div>
  </div>
);

/**
 * Compactar imagem da Biblioteca: escolhe a intensidade, o worker manda ao conversor de imagens
 * (imageconverter) e a tela compara a original com a versão compactada — lado a lado em tela
 * larga, uma sobre a outra no celular — para decidir qual fica. Documentação:
 * src/docs/midias/biblioteca.md ("Compactar imagem").
 */
export const MediaCompressDialog: React.FC<Props> = ({ media, isOpen, onClose, onAccepted }) => {
  const [meta, setMeta] = useState<MediaCompressionMeta | null>(null);
  const [current, setCurrent] = useState<MediaCompression | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [preset, setPreset] = useState<MediaCompressionPresetKey>('balanced');
  const [quality, setQuality] = useState(75);
  const [maxSide, setMaxSide] = useState('');
  const [zoom, setZoom] = useState<CompareZoom>('fit');
  const [confirmAccept, setConfirmAccept] = useState(false);

  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const syncing = useRef<HTMLDivElement | null>(null);

  // Abertura: opções + compactação que já esteja em andamento ou esperando decisão.
  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    setLoading(true);
    setCurrent(null);
    setFormError(null);
    setZoom('fit');
    setConfirmAccept(false);
    Promise.all([mediaService.compressionMeta(), mediaService.openCompression(media.id)])
      .then(([m, open]) => {
        if (!alive) return;
        setMeta(m);
        setCurrent(open);
      })
      .catch((err) => alive && setError(err))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [isOpen, media.id]);

  // Enquanto o worker trabalha, consulta a cada 2 s.
  useEffect(() => {
    if (!isOpen || !current || !isCompressionRunning(current.status)) return;
    const t = window.setTimeout(() => {
      mediaService
        .compression(media.id, current.id)
        .then(setCurrent)
        .catch(setError);
    }, 2000);
    return () => window.clearTimeout(t);
  }, [isOpen, current, media.id]);

  // Esc fecha (menos com a confirmação ou o erro abertos); trava a rolagem da página.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !error) {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose, error]);

  // Rolagem sincronizada no zoom: o mesmo ponto nos dois quadros.
  const sync = useCallback((from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to || syncing.current === from) {
      syncing.current = null;
      return;
    }
    syncing.current = to;
    to.scrollLeft = syncedScroll(from.scrollLeft, from.scrollWidth - from.clientWidth, to.scrollWidth - to.clientWidth);
    to.scrollTop = syncedScroll(from.scrollTop, from.scrollHeight - from.clientHeight, to.scrollHeight - to.clientHeight);
  }, []);

  const start = async () => {
    if (!meta) return;
    const built = buildCompressionRequest({ preset, quality, maxSide }, meta);
    if (built.error !== undefined) {
      setFormError(built.error);
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      setCurrent(await mediaService.compress(media.id, built.request));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  /** Descarta o candidato; `close` fecha a janela, senão volta às opções. */
  const discard = async (close: boolean) => {
    if (!current) return;
    setBusy(true);
    try {
      if (current.status === 'ready') await mediaService.discardCompression(media.id, current.id);
      setCurrent(null);
      setConfirmAccept(false);
      if (close) onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    if (!current) return;
    setBusy(true);
    try {
      const res = await mediaService.acceptCompression(media.id, current.id);
      setConfirmAccept(false);
      onAccepted(res);
      onClose();
    } catch (err) {
      setConfirmAccept(false);
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  const status = current?.status;
  const ready = status === 'ready' && current;
  const usage = current?.usage;
  const usageParts = [
    usage?.landing_pages.length ? `${usage.landing_pages.length} landing page${usage.landing_pages.length > 1 ? 's' : ''}` : '',
    usage?.email_campaigns.length ? `${usage.email_campaigns.length} e-mail${usage.email_campaigns.length > 1 ? 's' : ''} não enviado${usage.email_campaigns.length > 1 ? 's' : ''}` : '',
  ].filter(Boolean);
  const confirmMessage = current
    ? [
        `A imagem da Biblioteca passa a ser a versão compactada (${compressionSummary(current)}). Miniatura, prévia e versão do WhatsApp são refeitas.`,
        usageParts.length ? `Também muda em ${usageParts.join(' e ')}.` : '',
        usage?.has_public_copy ? 'E-mails já enviados continuam com a imagem antiga.' : '',
        'A original fica guardada por 7 dias e depois é apagada.',
      ]
        .filter(Boolean)
        .join(' ')
    : '';
  const width = compareWidth(zoom, current?.width, current?.original_width);

  let body: React.ReactNode;
  let footer: React.ReactNode;

  if (loading || !meta) {
    body = (
      <p className="py-16 text-center text-sm text-slate-500">
        <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
      </p>
    );
    footer = (
      <Button variant="outline" onClick={onClose}>
        Fechar
      </Button>
    );
  } else if (current && isCompressionRunning(status)) {
    body = (
      <div className="py-16 text-center space-y-3">
        <Loader2 size={32} className="mx-auto animate-spin text-emerald-600" />
        <p className="text-slate-700 font-medium">Compactando a imagem…</p>
        <p className="text-sm text-slate-500">
          Costuma levar poucos segundos. Pode fechar esta janela: a compactação continua e aparece aqui quando você voltar.
        </p>
      </div>
    );
    footer = (
      <Button variant="outline" onClick={onClose}>
        Fechar
      </Button>
    );
  } else if (current && (status === 'no_gain' || status === 'error')) {
    body = (
      <div className="py-10 max-w-xl mx-auto space-y-3 text-center">
        {status === 'no_gain' ? (
          <>
            <Info size={32} className="mx-auto text-sky-600" />
            <p className="text-slate-700 font-medium">A imagem já está bem compactada.</p>
            <p className="text-sm text-slate-500">
              Com estas opções a versão nova ficou só {formatGain(current.gain_percent)} menor
              {current.size_bytes !== undefined && ` (${formatBytes(current.original_size_bytes)} → ${formatBytes(current.size_bytes)})`} — menos que os{' '}
              {meta.min_gain_percent}% que valem a troca. Nada foi alterado. Tente a opção <strong>Forte</strong> ou um lado maior menor.
            </p>
          </>
        ) : (
          <>
            <AlertTriangle size={32} className="mx-auto text-red-600" />
            <p className="text-slate-700 font-medium">Não foi possível compactar.</p>
            <p className="text-sm text-red-700 [overflow-wrap:anywhere]">{current.error || 'Erro no conversor de imagens.'}</p>
          </>
        )}
      </div>
    );
    footer = (
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          Fechar
        </Button>
        <Button icon={<RotateCcw />} onClick={() => setCurrent(null)}>
          Tentar outra opção
        </Button>
      </div>
    );
  } else if (ready) {
    body = (
      <div className="flex flex-col gap-3 lg:h-full lg:min-h-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-sm text-slate-700">
            <strong>{compressionSummary(ready)}</strong>
            <span className="text-slate-500"> · qualidade {ready.quality}{ready.max_side ? `, lado maior até ${ready.max_side} px` : ''}</span>
          </p>
          <div className="inline-flex self-start rounded-lg border border-slate-300 overflow-hidden" role="group" aria-label="Zoom">
            {ZOOMS.map((z) => (
              <button
                key={z.key}
                type="button"
                onClick={() => setZoom(z.key)}
                className={clsx('px-3 py-1.5 text-sm', zoom === z.key ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}
              >
                {z.label}
              </button>
            ))}
          </div>
        </div>
        {zoom !== 'fit' && (
          <p className="text-xs text-slate-500">
            As duas aparecem no mesmo tamanho e rolam juntas: arraste ou role um quadro para conferir textos, bordas e detalhes.
          </p>
        )}
        <div className="flex flex-col lg:flex-row gap-3 lg:flex-1 lg:min-h-0">
          <Pane
            label="Original"
            spec={[formatBytes(ready.original_size_bytes), imageSpec(ready.original_width, ready.original_height, ready.original_content_type)].join(' · ')}
            url={ready.original_url}
            zoom={zoom}
            width={width}
            boxRef={leftRef}
            onScroll={() => sync(leftRef.current, rightRef.current)}
          />
          <Pane
            label="Compactada"
            spec={[formatBytes(ready.size_bytes), imageSpec(ready.width, ready.height, ready.content_type)].join(' · ')}
            badge={<span className="badge-success">{formatGain(ready.gain_percent)}</span>}
            url={ready.candidate_url}
            zoom={zoom}
            width={width}
            boxRef={rightRef}
            onScroll={() => sync(rightRef.current, leftRef.current)}
          />
        </div>
        {usage && (usage.landing_pages.length > 0 || usage.email_campaigns.length > 0) && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 space-y-1">
            <p>
              <strong>Usar a nova</strong> troca a imagem também em:
            </p>
            {usage.landing_pages.length > 0 && <p className="[overflow-wrap:anywhere]">Landing pages: {usage.landing_pages.join(', ')}</p>}
            {usage.email_campaigns.length > 0 && <p className="[overflow-wrap:anywhere]">E-mails não enviados: {usage.email_campaigns.join(', ')}</p>}
            <p className="text-xs text-amber-800">E-mails já enviados continuam com a imagem antiga.</p>
          </div>
        )}
      </div>
    );
    footer = confirmAccept ? (
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <p className="flex-1 text-sm text-slate-700">{confirmMessage}</p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 shrink-0">
          <Button variant="outline" onClick={() => setConfirmAccept(false)} disabled={busy}>
            Voltar
          </Button>
          <Button icon={<Check />} onClick={accept} loading={busy}>
            Confirmar: usar a nova
          </Button>
        </div>
      </div>
    ) : (
      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
        <Button variant="outline" icon={<RotateCcw />} onClick={() => discard(false)} disabled={busy}>
          Tentar outra opção
        </Button>
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={() => discard(true)} loading={busy}>
            Manter a original
          </Button>
          <Button icon={<Check />} onClick={() => setConfirmAccept(true)} disabled={busy}>
            Usar a nova
          </Button>
        </div>
      </div>
    );
  } else {
    // Opções.
    const options: { key: MediaCompressionPresetKey; label: string; description: string }[] = [
      ...meta.presets.map((p) => ({ key: p.key, label: p.label, description: p.description })),
      { key: 'custom', label: 'Personalizada', description: 'Você escolhe a qualidade e o lado maior.' },
    ];
    body = (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          Atual: <strong>{formatBytes(media.size_bytes)}</strong>
          {imageSpec(media.width, media.height, media.extension) && <> · {imageSpec(media.width, media.height, media.extension)}</>}
          {media.size_bytes > LARGE_IMAGE_BYTES && <span className="badge-warning ml-2">Grande para e-mail e site</span>}
        </div>
        {!meta.available && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            Conversor de imagens não configurado (Administração → Integrações → Conversor de imagens).
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Intensidade">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={preset === o.key}
              onClick={() => setPreset(o.key)}
              className={clsx(
                'p-3 rounded-lg border-2 text-left',
                preset === o.key ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300',
              )}
            >
              <span className="block font-semibold text-slate-800">{o.label}</span>
              <span className="block text-xs text-slate-500 mt-1">{o.description}</span>
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700" htmlFor="compress-quality">
                Qualidade: {quality}
              </label>
              <input
                id="compress-quality"
                type="range"
                min={meta.min_quality}
                max={meta.max_quality}
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                className="w-full accent-emerald-600"
              />
              <p className="text-xs text-slate-500">Menor = arquivo menor. Abaixo de 70 os detalhes finos começam a sumir.</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700" htmlFor="compress-side">
                Lado maior (px)
              </label>
              <input
                id="compress-side"
                className="input-field w-full"
                inputMode="numeric"
                placeholder="vazio = mantém o tamanho"
                value={maxSide}
                onChange={(e) => setMaxSide(e.target.value)}
              />
              <p className="text-xs text-slate-500">E-mail: 1200 px bastam. Site: até 2560 px. A imagem nunca é ampliada.</p>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500">
          Fotos saem em JPEG; imagens com transparência continuam PNG. Nada muda até você comparar e escolher <strong>Usar a nova</strong>.
        </p>
        {formError && <p className="text-sm text-red-700">{formError}</p>}
      </div>
    );
    footer = (
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button icon={<Minimize2 />} onClick={start} loading={busy} disabled={!meta.available}>
          Compactar
        </Button>
      </div>
    );
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch lg:items-center justify-center lg:p-6 animate-fade-in">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Compactar imagem"
          className={clsx('bg-white w-full flex flex-col lg:rounded-lg shadow-xl', ready ? 'h-full lg:h-[92vh] lg:max-w-7xl' : 'h-full lg:h-auto lg:max-h-[92vh] lg:max-w-3xl')}
        >
          <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-3 border-b border-slate-200">
            <div className="min-w-0">
              <h2 className="text-h3">Compactar imagem</h2>
              <p className="text-xs text-slate-500 truncate" title={media.original_file_name}>
                {media.title || media.original_file_name}
              </p>
            </div>
            <button type="button" onClick={onClose} aria-label="Fechar" className="p-1 text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>
          <div className={clsx('flex-1 min-h-0 px-4 sm:px-6 py-4 overflow-y-auto', ready && 'lg:overflow-hidden')}>{body}</div>
          <div className="px-4 sm:px-6 py-3 border-t border-slate-200 bg-slate-50 lg:rounded-b-lg">{footer}</div>
        </div>
      </div>
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </>,
    document.body,
  );
};

export default MediaCompressDialog;
